import { BlankLoading, LoadingFullPageOverlay } from "@/components/Loading"
import { NotificationsContext, VibratoClientContext } from "@/components/dashboard/contexts"
import CallService from "@/lib/calls"
import { defaultVoices } from "@/lib/default_voices"
import { handoffConditions, getHandoffConditionByPrompt } from "@/lib/handoff"
import { countryCodeInfo, localeInfo, supportedCountryCodes, supportedLocales } from "@/lib/locales"
import { formatAnyPhoneNumber } from "@/lib/phone"
import { RecipientService, TaskService } from "@/lib/tasks"
import { Call, CallbackPhoneNumber, CallerSettings, TwilioPhoneNumber, VibratoClient, Plan } from "@/lib/vibrato-api"
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import * as Iconoir from "iconoir-react"
import { EditPencil, Play, Plus, NavArrowDown } from "iconoir-react"
import moment from "moment"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import showdown from "showdown"
import { CurrentModel, idToVoice } from "../settings/VoiceSettings"
import CallerSettingsModal from "../CallerSettingsModal"
import { ScheduleCallModal } from "../ScheduleCallModal"
import CallForm, { FieldSettings } from "./CallForm"
import TourTrigger from "@/components/TourTrigger"
import usertour from "usertour.js"
import VerifyPhoneNumberModal from "../VerifyPhoneNumberModal"

const defaultRetries = "0"
const defaultRetryDelayMinutes = "1"

const SettingsCard = ({
  title,
  icon,
  children,
  className = "",
  titleClassName = "",
  headerActions,
  defaultOpen = true
}: {
  title?: string
  icon?: React.ReactNode
  children: React.ReactNode
  className?: string
  titleClassName?: string
  headerActions?: React.ReactNode
  defaultOpen?: boolean
}) => {
  return (
    <div className={`bg-white shadow-sm rounded-xl border border-gray-200/80 overflow-hidden ${className}`}>
      <Disclosure defaultOpen={defaultOpen}>
        {({ open }) => (
          <>
            <DisclosureButton className="flex w-full justify-between items-center p-5 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                {icon && <div className="p-2 bg-purple-100 rounded-lg">{icon}</div>}
                <h3 className={`text-base font-medium text-gray-900 ${titleClassName}`}>{title}</h3>
              </div>
              <div className="flex items-center gap-2">
                {headerActions}
                <NavArrowDown
                  className={`${
                    open ? "rotate-180 transform" : ""
                  } h-5 w-5 text-blue-500 transition-transform duration-200`}
                />
              </div>
            </DisclosureButton>
            <DisclosurePanel className="border-t border-gray-200 p-5">{children}</DisclosurePanel>
          </>
        )}
      </Disclosure>
    </div>
  )
}

const TaskInfoCard = ({
  call,
  taskType,
  callService,
  recSvc,
  scheduledAt,
  setShowScheduleModal,
  setShowDynamicTaskModal,
  audioElement,
  setAudioElement,
  playingVoiceSlug,
  setPlayingVoiceSlug,
  loadingVoiceSlug,
  setLoadingVoiceSlug,
  vibratoClient
}: {
  call: any
  taskType: string
  callService: any
  recSvc: any
  scheduledAt: string | null
  setShowScheduleModal: (show: boolean) => void
  setShowDynamicTaskModal: (show: boolean) => void
  audioElement: HTMLAudioElement | null
  setAudioElement: (audio: HTMLAudioElement | null) => void
  playingVoiceSlug: string | null
  setPlayingVoiceSlug: (slug: string | null) => void
  loadingVoiceSlug: string | null
  setLoadingVoiceSlug: (slug: string | null) => void
  vibratoClient: VibratoClient
}) => {
  // @ts-expect-error - Iconoir type definition doesn't support dynamic access
  const Logo = call.task.iconoir_logo ? Iconoir[call.task.iconoir_logo] : null
  const converter = new showdown.Converter()

  return (
    <div className="bg-white shadow-sm rounded-xl border border-gray-200/80 overflow-hidden">
      <Disclosure defaultOpen>
        {({ open }) => (
          <>
            <DisclosureButton className="flex w-full justify-between items-center p-5 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Iconoir.Phone className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-base font-medium text-gray-900 flex items-center gap-2">
                  {taskType === "dynamic" && (
                    <>
                      {call.dynamic_task.task_short}
                      {Logo && <Logo />}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowDynamicTaskModal(true)
                        }}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <EditPencil width={18} height={18} />
                      </button>
                    </>
                  )}
                  {taskType === "template" && (
                    <>
                      {!call.task_template.internal && (
                        <Link
                          to={`/task-templates/${call.task_template.uuid}`}
                          className="text-blue-600 hover:underline"
                        >
                          {call.task_template.task_name}
                        </Link>
                      )}
                      {call.task_template.internal && <span> {call.task_template.task_name}</span>}
                      {Logo && <Logo />}
                    </>
                  )}
                  {taskType === "normal" && (
                    <>
                      {call.task.name}
                      {Logo && <Logo />}
                    </>
                  )}
                  {taskType === "prank" && (
                    <>
                      {callService.getFrontendName()}
                      {Logo && <Logo />}
                    </>
                  )}
                </h3>
              </div>
              <NavArrowDown
                className={`${
                  open ? "rotate-180 transform" : ""
                } h-5 w-5 text-blue-500 transition-transform duration-200`}
              />
            </DisclosureButton>
            <DisclosurePanel className="border-t border-gray-200 p-5">
              <div className="flex flex-col gap-2 md:gap-4 w-full">
                {taskType === "dynamic" && (
                  <>
                    <div>
                      <div
                        className="dynamic-task-markdown opacity-50 max-h-[50px] overflow-hidden relative after:absolute after:bottom-0 after:h-12 after:w-full after:bg-linear-to-t after:from-white after:to-transparent"
                        onClick={() => {
                          setShowDynamicTaskModal(true)
                        }}
                        dangerouslySetInnerHTML={{
                          __html: converter.makeHtml(call.dynamic_task.task)
                        }}
                      />
                    </div>
                    {call.dynamic_task.recipient_name && (
                      <div>
                        <p className="mb-1 md:mb-2 font-medium">Recipient</p>
                        <p className="font-light">{call.dynamic_task.recipient_name}</p>
                      </div>
                    )}
                  </>
                )}
                {taskType === "template" && (
                  <>
                    <div>
                      <p className="text-sm text-gray-600">{call.task_template.task_description}</p>
                    </div>
                    {call.contact && (
                      <div>
                        <p className="mb-2 font-medium">Contact</p>
                        <Link to={`/contacts/${call.contact.uuid}`} className="text-blue-600 hover:underline">
                          {call.contact.display_name}
                        </Link>
                      </div>
                    )}
                  </>
                )}
                {taskType === "normal" && (
                  <>
                    <div>
                      <p className="text-sm text-gray-600">{call.task.description}</p>
                    </div>
                    {recSvc.getRecipient().slug !== "anyone" && (
                      <div>
                        <p className="mb-2 font-medium">Recipient</p>
                        <p className="font-light">{recSvc.getRecipient().name}</p>
                        {recSvc.getRecipient().name !== recSvc.getRecipient().description_short && (
                          <p className="text-sm font-thin mt-2 text-gray-700">
                            {recSvc.getRecipient().description_short}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
                {taskType === "prank" && (
                  <>
                    <div>
                      <div className="flex items-start gap-4">
                        <div>
                          <p className="text-sm text-gray-600">{callService.getFrontendDescription()}</p>
                          {call.task_parameters?.["voice-slug"] && (
                            <button
                              type="button"
                              onClick={async () => {
                                if (playingVoiceSlug === call.task_parameters["voice-slug"]) {
                                  if (audioElement) {
                                    audioElement.pause()
                                    setPlayingVoiceSlug(null)
                                  }
                                  return
                                }

                                try {
                                  setLoadingVoiceSlug(call.task_parameters["voice-slug"])
                                  const audioUrl = await vibratoClient.getVoiceDemo(call.task_parameters["voice-slug"])
                                  if (audioElement) {
                                    audioElement.pause()
                                  }
                                  const audio = new Audio(audioUrl.audioUrl)
                                  setAudioElement(audio)
                                  audio.onended = () => {
                                    setPlayingVoiceSlug(null)
                                  }
                                  await audio.play()
                                  setPlayingVoiceSlug(call.task_parameters["voice-slug"])
                                } catch (error) {
                                  console.error("Error playing voice demo:", error)
                                } finally {
                                  setLoadingVoiceSlug(null)
                                }
                              }}
                              className="inline-flex mt-2 w-min items-center justify-center p-2 rounded-full text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
                              disabled={loadingVoiceSlug === call.task_parameters["voice-slug"]}
                            >
                              {loadingVoiceSlug === call.task_parameters["voice-slug"] ? (
                                <svg
                                  className="animate-spin h-6 w-6 text-blue-600"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                              ) : playingVoiceSlug === call.task_parameters["voice-slug"] ? (
                                <svg
                                  className="h-6 w-6 text-blue-600"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <rect x="6" y="4" width="4" height="16" fill="currentColor" />
                                  <rect x="14" y="4" width="4" height="16" fill="currentColor" />
                                </svg>
                              ) : (
                                <Play className="h-6 w-6 text-blue-600" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    {recSvc.getRecipient().slug !== "anyone" && (
                      <div>
                        <p className="mb-2 font-medium">Recipient</p>
                        <p className="font-light">{recSvc.getRecipient().name}</p>
                        {recSvc.getRecipient().name !== recSvc.getRecipient().description_short && (
                          <p className="text-sm font-thin mt-2 text-gray-700">
                            {recSvc.getRecipient().description_short}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
                {scheduledAt && (
                  <div className="flex justify-between items-center mt-4">
                    <div className="mb-2">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">Scheduled For</p>
                        <button
                          type="button"
                          onClick={() => setShowScheduleModal(true)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <EditPencil width={16} height={16} />
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <p className="flex font-light gap-2">{moment(scheduledAt).format("MMM D, YYYY h:mm A")}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </div>
  )
}

function CallSettingsCard({
  call,
  callerSettings,
  twilioPhoneNumbers,
  hiddenSections,
  restoreSection,
  removedSections,
  triggerSettingsTour,
  setShowCallerSettingsModal,
  reloadCallerSettings,
  callbackPhoneNumbers,
  setShowVerifyCallbackNumberModal
}: {
  call: any
  callerSettings: any
  twilioPhoneNumbers: any
  hiddenSections: any
  restoreSection: any
  removedSections: any
  triggerSettingsTour: any
  setShowCallerSettingsModal: any
  reloadCallerSettings: any
  callbackPhoneNumbers: any
  setShowVerifyCallbackNumberModal: (show: boolean) => void
}) {
  return (
    <SettingsCard
      title="Call Settings"
      icon={<Iconoir.Settings className="w-5 h-5 text-purple-600" />}
      className="mt-4"
      headerActions={
        <TourTrigger
          onClick={(e) => {
            e.preventDefault()
            triggerSettingsTour()
          }}
          tooltipContent="Learn about the different call settings."
        />
      }
    >
      <div id="callSettings">
        <CallForm
          fields={callSettingsForTaskRecipient({
            call,
            slug: call.task_recipient_slug,
            callerSettings,
            disablePhoneFields: call.task_template ? call.task_template.internal : false,
            twilioPhoneNumbers
          })}
          values={call}
          sectionTitle={null}
        >
          {/* Voice and AI Settings */}
          {call.task.slug !== "prank_call" && !hiddenSections["voice-ai-settings"] && (
            <Disclosure>
              {({ open }) => (
                <div>
                  <DisclosureButton
                    id="voice-ai-settings-disclosure-button"
                    className="flex w-full justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-800">Voice & AI Settings</span>
                    <NavArrowDown
                      className={`${
                        open ? "rotate-180 transform" : ""
                      } h-6 w-6 text-gray-600 transition-transform duration-200 shrink-0`}
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="py-4 px-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      {/* Voice Settings */}
                      <div id="voice-settings-card" className="bg-blue-50 rounded-lg p-3 md:p-4 flex flex-col w-full">
                        <div className="flex items-center mb-2 w-full">
                          <h3 className="text-base font-semibold text-blue-800 grow">Voice</h3>
                          <div className="ml-auto">
                            <button
                              type="button"
                              className="text-blue-600 hover:text-blue-800"
                              onClick={() => setShowCallerSettingsModal(true)}
                            >
                              <EditPencil width={16} height={16} />
                            </button>
                          </div>
                        </div>
                        {callerSettings?.voice_id ? (
                          <p className="text-sm text-gray-700 font-medium">
                            {idToVoice(callerSettings.voice_id)?.name}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-700 font-medium">Voice clone</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">The voice that will speak during this call</p>
                      </div>

                      {/* AI Model Settings */}
                      <div id="ai-settings-card" className="bg-purple-50 rounded-lg p-3 md:p-4 flex flex-col w-full">
                        <div className="flex items-center mb-2 w-full">
                          <h3 className="text-base font-semibold text-purple-800 grow">AI Intelligence</h3>
                          <div className="ml-auto">
                            <button
                              type="button"
                              className="text-purple-600 hover:text-purple-800"
                              onClick={() => setShowCallerSettingsModal(true)}
                            >
                              <EditPencil width={16} height={16} />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 font-medium">
                          <CurrentModel reload={reloadCallerSettings} />
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Determines how smart and responsive your AI caller will be
                        </p>
                      </div>
                    </div>
                  </DisclosurePanel>
                </div>
              )}
            </Disclosure>
          )}

          {/* Voicemail Settings */}
          {!hiddenSections["voicemail-settings"] && (
            <Disclosure>
              {({ open }) => (
                <div>
                  <DisclosureButton
                    id="voicemail-settings-disclosure-button"
                    className="flex w-full justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-800">Voicemail Settings</span>
                    <NavArrowDown
                      className={`${
                        open ? "rotate-180 transform" : ""
                      } h-6 w-6 text-gray-600 transition-transform duration-200 shrink-0`}
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="py-4 px-1">
                    <div id="voicemail-settings-card" className="bg-green-100 rounded-2xl p-4 md:p-6 w-full">
                      <div className="flex items-center mb-3 w-full">
                        <h3 className="text-base font-semibold text-green-800 grow">Leave a Voicemail</h3>
                      </div>
                      <p className="text-xs text-gray-600 mb-3">
                        When enabled, your AI caller will leave a voicemail message if no one answers the call.
                      </p>
                      <label className="flex items-center p-2 md:p-3 bg-green-50 cursor-pointer rounded-lg">
                        <input
                          type="checkbox"
                          id="leave_voicemail_message"
                          name="leave_voicemail_message"
                          className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          defaultChecked={call.leave_voicemail_message}
                        />
                        <span className="block text-sm font-medium text-green-800">Enable voicemail</span>
                      </label>

                      <div className="mt-4 md:mt-6">
                        <div className="flex items-center mb-3">
                          <h3 className="text-base font-semibold text-green-800">Voicemail Message</h3>
                        </div>
                        <p className="text-xs text-gray-600 mb-3">
                          Customize the message that will be left if no one answers. If left blank, we&apos;ll generate
                          an appropriate message.
                        </p>
                        <textarea
                          id="voicemail_message"
                          name="voicemail_message"
                          className="w-full px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          rows={4}
                          placeholder="Hi, this is David, call me back."
                          defaultValue={call.voicemail_message}
                        />
                      </div>
                    </div>
                  </DisclosurePanel>
                </div>
              )}
            </Disclosure>
          )}

          {/* Handoff Settings */}
          {!hiddenSections["handoff-settings"] && (
            <Disclosure>
              {({ open }) => (
                <div>
                  <DisclosureButton
                    id="handoff-settings-disclosure-button"
                    className="flex w-full justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-800">Handoff Settings</span>
                    <NavArrowDown
                      className={`${
                        open ? "rotate-180 transform" : ""
                      } h-6 w-6 text-gray-600 transition-transform duration-200 shrink-0`}
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="py-4 px-1">
                    <div id="handoff-settings-card" className="bg-amber-100 rounded-2xl p-4 md:p-6 w-full">
                      <div className="flex items-center mb-3 w-full">
                        <h3 className="text-base font-semibold text-amber-800 grow">Live Call Handoff</h3>
                      </div>
                      <p className="text-xs text-gray-600 mb-3">
                        Define a trigger condition that will seamlessly transfer the call to you when met. This allows
                        for real-time intervention exactly when needed.
                      </p>
                      <select
                        id="handoff_condition"
                        name="handoff_condition"
                        className="py-2 md:py-3 px-3 md:px-4 block w-full border border-gray-200 bg-white rounded-md text-base focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400"
                        onChange={(e) => {
                          const customTextarea = document.getElementById("custom_handoff_condition")
                          if (customTextarea) {
                            customTextarea.style.display = e.target.value === "custom" ? "block" : "none"
                          }

                          // Show the description for the selected option
                          const descriptionElement = document.getElementById("handoff_condition_description")
                          if (descriptionElement && e.target.value && e.target.value !== "custom") {
                            // Find the selected handoff condition
                            const selectedCondition = getHandoffConditionByPrompt(e.target.value)

                            if (selectedCondition) {
                              // Update the title
                              const titleElement = descriptionElement.querySelector("h4")
                              if (titleElement) {
                                titleElement.textContent = `What happens when handoff is set to "${selectedCondition.title}":`
                              }

                              // Use the summary from the handoff condition
                              const contentContainer = descriptionElement.querySelector(".description-content")
                              if (contentContainer) {
                                contentContainer.innerHTML = `<p class="text-sm">${selectedCondition.summary}</p>`
                              }

                              descriptionElement.style.display = "block"
                            }
                          } else if (descriptionElement) {
                            descriptionElement.style.display = "none"
                          }
                        }}
                      >
                        <option value="">Select a handoff condition</option>
                        {handoffConditions.map((condition) => (
                          <option key={condition.id} value={condition.prompt}>
                            {condition.title}
                          </option>
                        ))}
                        <option value="custom">Custom condition</option>
                      </select>

                      {/* Description display for selected handoff condition */}
                      <div
                        id="handoff_condition_description"
                        className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 leading-relaxed"
                        style={{ display: "none" }}
                      >
                        <h4 className="font-medium text-amber-900 mb-3">What happens during the call:</h4>
                        {/* Content will be inserted dynamically */}
                        <div className="description-content space-y-3">{/* Paragraphs will be inserted here */}</div>
                      </div>

                      <textarea
                        id="custom_handoff_condition"
                        name="custom_handoff_condition"
                        className="py-2 md:py-3 px-3 md:px-4 block w-full border border-gray-200 bg-white rounded-md text-base focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400 mt-3"
                        rows={3}
                        placeholder="Be specific about triggers for handoff (e.g., 'When they ask about pricing above $500' or 'If they mention needing to speak with a manager'). Use clear, actionable language that the AI can easily identify."
                        style={{ display: "none" }}
                      />

                      <div className="mt-6">
                        <div className="flex items-center mb-3">
                          <h3 className="text-base font-semibold text-amber-800">Handoff Phone Number</h3>
                        </div>
                        <p className="text-xs text-gray-600 mb-3">
                          Select which of your verified phone numbers should receive the call when the handoff condition
                          is met.
                        </p>
                        {callbackPhoneNumbers && callbackPhoneNumbers.length > 0 ? (
                          <>
                            <select
                              id="handoff_phone_number_uuid"
                              name="handoff_phone_number_uuid"
                              className="py-2 md:py-3 px-3 md:px-4 block w-full border border-gray-200 bg-white rounded-md text-base focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400"
                            >
                              {callbackPhoneNumbers.map((number: any) => (
                                <option key={number.uuid} value={number.uuid}>
                                  {formatAnyPhoneNumber(number.country_code, number.phone_number)}
                                </option>
                              ))}
                            </select>
                            <p className="mt-3 text-xs text-amber-700">
                              <button
                                type="button"
                                onClick={() => setShowVerifyCallbackNumberModal(true)}
                                className="text-amber-700 hover:text-amber-800 font-medium underline"
                              >
                                Add a callback number
                              </button>
                            </p>
                          </>
                        ) : (
                          <div className="text-center text-sm text-gray-500 p-4 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="mb-2 text-amber-800">No callback numbers available.</p>
                            <button
                              type="button"
                              onClick={() => setShowVerifyCallbackNumberModal(true)}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              Click here to add a callback number.
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </DisclosurePanel>
                </div>
              )}
            </Disclosure>
          )}

          {/* Retry Settings */}
          {!hiddenSections["retry-settings"] && (
            <Disclosure>
              {({ open }) => (
                <div>
                  <DisclosureButton
                    id="retry-settings-disclosure-button"
                    className="flex w-full justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-800">Retry Settings</span>
                    <NavArrowDown
                      className={`${
                        open ? "rotate-180 transform" : ""
                      } h-6 w-6 text-gray-600 transition-transform duration-200 shrink-0`}
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="py-4 px-1">
                    <div id="retry-settings-card" className="bg-orange-100 rounded-2xl p-4 md:p-6 w-full">
                      <div className="flex items-center mb-3 w-full">
                        <h3 className="text-base font-semibold text-orange-800 grow">Retry Configuration</h3>
                      </div>
                      <p className="text-xs text-gray-600 mb-3">
                        Configure how many times the system should retry the call if it fails to connect and how long to
                        wait between attempts.
                      </p>

                      <div className="mb-4">
                        <label htmlFor="retries" className="block mb-2 text-sm font-medium text-gray-700">
                          Number of retries
                        </label>
                        <select
                          id="retries"
                          name="retries"
                          className="py-2 md:py-3 px-3 md:px-4 block w-full border border-gray-200 bg-white rounded-md text-base focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400"
                          defaultValue={defaultRetries}
                          required
                        >
                          <option value="0">0 - No retries</option>
                          <option value="1">1 retry</option>
                          <option value="2">2 retries</option>
                          <option value="3">3 retries</option>
                          <option value="4">4 retries</option>
                          <option value="5">5 retries</option>
                        </select>
                      </div>

                      <div className="mb-2">
                        <label htmlFor="retry_delay_minutes" className="block mb-2 text-sm font-medium text-gray-700">
                          Delay between retries
                        </label>
                        <select
                          id="retry_delay_minutes"
                          name="retry_delay_minutes"
                          className="py-2 md:py-3 px-3 md:px-4 block w-full border border-gray-200 bg-white rounded-md text-base focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400"
                          defaultValue={defaultRetryDelayMinutes}
                          required
                        >
                          <option value="1">1 minute</option>
                          <option value="5">5 minutes</option>
                          <option value="10">10 minutes</option>
                          <option value="15">15 minutes</option>
                          <option value="30">30 minutes</option>
                          <option value="60">1 hour</option>
                          <option value="120">2 hours</option>
                          <option value="240">4 hours</option>
                          <option value="480">8 hours</option>
                          <option value="1440">24 hours</option>
                        </select>
                      </div>
                    </div>
                  </DisclosurePanel>
                </div>
              )}
            </Disclosure>
          )}

          <div className="mt-2">
            <button
              type="button"
              onClick={triggerSettingsTour}
              className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
            >
              <Iconoir.HelpCircle className="w-4 h-4 mr-1" />
              Explain these settings
            </button>
          </div>

          {/* Restore removed sections */}
          {removedSections.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm font-medium mb-2 flex items-center gap-2">
                <span className="text-blue-600">
                  <Plus width={16} height={16} />
                </span>
                Restore removed settings:
              </p>
              <div className="flex flex-wrap gap-2">
                {removedSections.map((sectionId: string) => {
                  let sectionName = ""
                  switch (sectionId) {
                    case "voice-ai-settings":
                      sectionName = "Voice and AI Settings"
                      break
                    case "voicemail-settings":
                      sectionName = "Voicemail Settings"
                      break
                    case "handoff-settings":
                      sectionName = "Handoff Settings"
                      break
                    case "retry-settings":
                      sectionName = "Retry Settings"
                      break
                    default:
                      return null
                  }

                  return (
                    <button
                      key={sectionId}
                      type="button"
                      onClick={() => restoreSection(sectionId)}
                      className="inline-flex items-center px-3 py-2 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md border border-blue-200 transition-colors"
                    >
                      <Plus width={14} height={14} className="mr-2" />
                      {sectionName}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </CallForm>
      </div>
    </SettingsCard>
  )
}

function CallInformationCard({ fields, values }: { fields: any[]; values: any }) {
  if (!fields || fields.length === 0) {
    return null
  }

  return (
    <SettingsCard
      title="Call Information"
      icon={<Iconoir.InfoCircle className="w-5 h-5 text-purple-600" />}
      className="mt-4"
    >
      <div id="callInformationParameters">
        <CallForm fields={fields} values={values} sectionTitle={null} allowRemovableFields />
      </div>
    </SettingsCard>
  )
}

function taskTemplateParamsToTaskParameters(taskTemplateParams: any): any {
  if (!taskTemplateParams || !Array.isArray(taskTemplateParams)) {
    return {}
  }

  return taskTemplateParams.reduce((acc: any, param: any) => {
    if (param.slug && param.value !== undefined) {
      acc[param.slug] = param.value
    }
    return acc
  }, {})
}

export default function CallSettings({
  callId,
  initialCall,
  showScheduleModal,
  setShowScheduleModal,
  scheduledAt,
  setScheduledAt,
  setShowVerifyPhoneModal,
  setShowDynamicTaskModal,
  setIsPlacingCall,
  plan
}: {
  callId: string
  initialCall: any
  showScheduleModal: boolean
  setShowScheduleModal: (show: boolean) => void
  scheduledAt: string | null
  setScheduledAt: (scheduledAt: string | null) => void
  setShowVerifyPhoneModal: (show: boolean) => void
  setShowDynamicTaskModal: (show: boolean) => void
  setIsPlacingCall: (isPlacing: boolean) => void
  plan: Plan | null
}): React.JSX.Element {
  const navigate = useNavigate()
  const vibratoClient: VibratoClient = useContext(VibratoClientContext)
  const [call, setCall] = useState<any>(null)
  const [taskSvc, setTaskSvc] = useState<TaskService | null>(null)
  const [recSvc, setRecSvc] = useState<RecipientService | null>(null)
  const [showCallerSettingsModal, setShowCallerSettingsModal] = useState(false)
  const [callerSettings, setCallerSettings] = useState<CallerSettings | null>(null)
  const [reloadCallerSettings, setReloadCallerSettings] = useState(0)
  const [twilioPhoneNumbers, setTwilioPhoneNumbers] = useState<TwilioPhoneNumber[]>([])
  const [callbackPhoneNumbers, setCallbackPhoneNumbers] = useState<CallbackPhoneNumber[]>([])
  const [playingVoiceSlug, setPlayingVoiceSlug] = useState<string | null>(null)
  const [loadingVoiceSlug, setLoadingVoiceSlug] = useState<string | null>(null)
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null)
  const [showConsentModal, setShowConsentModal] = useState(false)
  const [showVerifyCallbackNumberModal, setShowVerifyCallbackNumberModal] = useState(false)
  const [showLanguageMismatchModal, setShowLanguageMismatchModal] = useState(false)
  const [formEventForLanguageMismatch, setFormEventForLanguageMismatch] = useState<React.FormEvent | null>(null)
  const [selectedLocale, setSelectedLocale] = useState<string | null>(null)
  const [defaultLocale, setDefaultLocale] = useState<string | null>(null)

  const notifications = useContext(NotificationsContext)

  // Track which advanced settings sections are hidden
  const [hiddenSections, setHiddenSections] = useState<Record<string, boolean>>({})
  const [removedSections, setRemovedSections] = useState<string[]>([])

  const restoreSection = (sectionId: string) => {
    setHiddenSections((prev) => ({
      ...prev,
      [sectionId]: false
    }))
    setRemovedSections((prev) => prev.filter((id) => id !== sectionId))
  }

  const reloadCallerSettingsCallback = useCallback(() => {
    setReloadCallerSettings(reloadCallerSettings + 1)
  }, [reloadCallerSettings])

  const triggerSettingsTour = useCallback(() => {
    const disclosureButtonIds = [
      "voice-ai-settings-disclosure-button",
      "voicemail-settings-disclosure-button",
      "handoff-settings-disclosure-button",
      "retry-settings-disclosure-button"
    ]

    disclosureButtonIds.forEach((id) => {
      const button = document.getElementById(id)
      if (button && button.getAttribute("aria-expanded") === "false") {
        button.click()
      }
    })

    // Delay to allow disclosures to open
    setTimeout(() => {
      usertour.start("cmcmhg43d02xoa3ms6e4i13vr")
    }, 300)
  }, [])

  useEffect(() => {
    if (typeof initialCall !== "undefined") {
      setCall(initialCall)
    }

    if (vibratoClient == null) {
      return
    }

    vibratoClient.getCall(parseInt(callId)).then((call: any) => {
      setCall(call)
      setTaskSvc(new TaskService(call.task))
      setRecSvc(new RecipientService(call.task_recipient))

      if (call.task.slug == "custom_call") {
        notifications({
          type: "warning",
          header: "Warning:",
          id: "call-settings-error",
          message: "This is a low-level way of defining a call. For most cases, use the ChatGPT integration instead."
        })
      }
    })

    vibratoClient
      .getTwilioPhoneNumbers()
      .then((numbers) => {
        setTwilioPhoneNumbers(numbers)
      })
      .catch((error) => {
        console.error("Error fetching Twilio phone numbers:", error)
      })

    vibratoClient
      .getCallbackPhoneNumbers()
      .then((numbers) => {
        setCallbackPhoneNumbers(numbers)
      })
      .catch((error) => {
        console.error("Error fetching callback phone numbers:", error)
      })
  }, [initialCall, callId, vibratoClient, setTwilioPhoneNumbers, setCallbackPhoneNumbers])

  useEffect(() => {
    vibratoClient
      .getCallerSettings()
      .then((data) => {
        setCallerSettings(data)
      })
      .catch((error) => {
        console.error("Error fetching caller settings:", error)
      })
  }, [setCallerSettings, reloadCallerSettings, vibratoClient])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const form = e.target as HTMLFormElement
    const countryCodeValue = (form.elements.namedItem("country_code") as HTMLSelectElement)?.value
    const localeValue = (form.elements.namedItem("locale") as HTMLSelectElement)?.value

    if (countryCodeValue && localeValue) {
      const countryInfo = Object.values(countryCodeInfo).find((info) => info.code === countryCodeValue)
      if (countryInfo && countryInfo.default_locale !== localeValue) {
        setFormEventForLanguageMismatch(e)
        setSelectedLocale(localeValue)
        setDefaultLocale(countryInfo.default_locale)
        setShowLanguageMismatchModal(true)
        return
      }
    }

    if (call.task_slug === "prank_call") {
      setShowConsentModal(true)
      return
    }

    // Proceed with normal call placement
    await placeCall(e)
  }

  const placeCall = async (e: React.FormEvent) => {
    setIsPlacingCall(true)
    try {
      await updateSettings(e as React.FormEvent<HTMLFormElement>)
    } catch (e) {
      console.error("Error updating call settings: ", e)
      throw e
    } finally {
      setIsPlacingCall(false)
    }
  }

  async function updateSettings(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault() // Prevents the default form submission behavior

    if (vibratoClient == null) {
      return
    }

    if (plan?.needs_phone_verification) {
      setShowVerifyPhoneModal(true)
      return
    }

    const form = event.target as HTMLFormElement
    const callInformationInputs = form.querySelectorAll(
      "#callInformationParameters input, #callInformationParameters textarea, #callInformationParameters select"
    )
    const callSettingInputs = form.querySelectorAll("#callSettings input, #callSettings textarea, #callSettings select")

    function getInputsValues(inputs: NodeListOf<Element>, submitBlank: boolean): Record<string, any> {
      const values: Record<string, any> = {}

      inputs.forEach((element: any) => {
        if (element.type === "checkbox") {
          values[element.id] = true && element.checked
          return
        }

        if (element.type === "tel") {
          values[element.id] = element.value.replace(/\D/g, "")
          return
        }

        if (element.value === "") {
          if (!submitBlank) {
            return
          }
        }

        values[element.id] = element.value
      })

      return values
    }

    // Gather settings, including 'outbound_twilio_phone_number_uuid' if selected
    const settings = getInputsValues(callSettingInputs, true)
    // if (settings.outbound_phone_number_uuid) {
    //   settings.outbound_phone_number = {
    //     uuid: settings.outbound_phone_number_uuid
    //   }
    //   delete settings.outbound_phone
    // }
    if (settings.outbound_twilio_phone_number_uuid) {
      settings.outbound_twilio_phone_number = {
        uuid: settings.outbound_twilio_phone_number_uuid
      }
      delete settings.outbound_twilio_phone_number
    }

    const allInfoValues = getInputsValues(callInformationInputs, false)
    const newCallParameters: Record<string, any> = {
      task_parameters: {},
      task_missing_data_parameters: {},
      task_recipient_parameters: {},
      task_template_parameters: []
    }

    allCallInfoFields.forEach((field: any) => {
      const slug = field.html_id
      const value = allInfoValues[slug]

      if (value === undefined) {
        return
      }

      switch (field.source) {
        case "recipient":
          newCallParameters.task_recipient_parameters[slug] = value
          break
        case "task":
          newCallParameters.task_parameters[slug] = value
          break
        case "missing":
          newCallParameters.task_missing_data_parameters[slug] = value
          break
        case "dynamic":
          newCallParameters.task_parameters[slug] = value
          break
        case "template":
          newCallParameters.task_template_parameters.push({ slug, value })
          break
      }
    })

    settings["task_recipient_parameters"] = newCallParameters.task_recipient_parameters
    settings["task_parameters"] = newCallParameters.task_parameters
    settings["task_missing_data_parameters"] = newCallParameters.task_missing_data_parameters
    settings["task_template_parameters"] = newCallParameters.task_template_parameters

    if (call.task.slug === "prank_call") {
      const voiceSlug = call?.task_parameters?.["voice-slug"]
      const callTypeSlug = call?.task_parameters?.["call-type-slug"]
      if (voiceSlug && callTypeSlug) {
        settings["task_parameters"] = {
          ...settings["task_parameters"],
          "voice-slug": voiceSlug,
          "call-type-slug": callTypeSlug
        }
      }
    }

    if (scheduledAt) {
      settings["scheduled_at"] = scheduledAt
    }

    settings["retries"] = parseInt(form.querySelector<HTMLInputElement>("#retries")?.value || defaultRetries)

    const minutes = parseInt(
      form.querySelector<HTMLInputElement>("#retry_delay_minutes")?.value || defaultRetryDelayMinutes
    )
    settings["retry_delay_seconds"] = minutes * 60

    if (settings.handoff_condition?.trim() === "") {
      delete settings.handoff_phone_number_uuid
      delete settings.handoff_condition
    }

    if (settings.handoff_condition == "custom") {
      settings.handoff_condition = settings.custom_handoff_condition
      delete settings.custom_handoff_condition
    }

    try {
      await vibratoClient.updateCall(parseInt(callId), settings)
      await vibratoClient.execCall(parseInt(callId))
    } catch (e: any) {
      const msg = e?.message ? e.message : e
      console.log("Error updating call settings: ", msg)

      const tokens: Array<string> = []
      let bannerMsg = ""
      try {
        const error = JSON.parse(e.message)
        // For each KV append to strMessage
        for (const key in error) {
          tokens.push(`${key}: ${error[key]}`)
        }
        bannerMsg = tokens.join(", ")
      } catch (e) {
        console.log("Error parsing JSON", e)
        bannerMsg = msg
      }

      notifications({
        type: "error",
        header: "Error:",
        id: "call-settings-error",
        message: bannerMsg
      })

      return
    }

    navigate(`/calls/${call.id}`)
  }

  const allCallInfoFields = React.useMemo(() => {
    if (!call) return []

    const fields: any[] = []
    const seenSlugs = new Set()

    const processSource = (sourceFields: any, sourceName: string, removable: boolean, required?: boolean) => {
      if (!sourceFields || !Array.isArray(sourceFields)) return

      for (const param of sourceFields) {
        const slug = param.slug || param.html_id || (param.name && param.name.replace(/\W/g, "_").toLowerCase())
        if (!slug || seenSlugs.has(slug)) continue
        seenSlugs.add(slug)

        fields.push({
          ...param,
          html_id: slug,
          source: sourceName,
          removable,
          required: required !== undefined ? required : param.required
        })
      }
    }

    // 1. Recipient Params (not removable, required)
    processSource(call.task_recipient?.params, "recipient", false, true)

    // 2. Main Task Params
    let taskParams = call.task?.params || []
    if (call.task.slug === "prank_call") {
      taskParams = taskParams.filter((p: any) => p.slug !== "voice-slug" && p.slug !== "call-type-slug")
    }
    processSource(taskParams, "task", true, undefined)

    // 3. Missing Data (removable, required)
    const missingDataAsFields = (call.missing_data_from_previous_call || []).map((p: any) => ({
      name: p.name,
      description: p.description,
      slug: p.slug || p.name.replace(/\W/g, "_").toLowerCase(),
      html_type: "text"
    }))
    processSource(missingDataAsFields, "missing", true, true)

    // 4. Task Template (removable)
    processSource(call.task_template?.full_task_properties, "template", true, undefined)

    // 5. Dynamic Task (removable, not required)
    processSource(call.dynamic_task?.task_parameters, "dynamic", true, false)

    return fields
  }, [call])

  const allCallInfoValues = React.useMemo(() => {
    if (!call) return {}

    const templateValues = taskTemplateParamsToTaskParameters(call.task_template_parameters)

    return {
      ...templateValues,
      ...call.task_missing_data_parameters,
      ...call.task_parameters,
      ...call.task_recipient_parameters
    }
  }, [call])

  if (taskSvc == null) {
    return <BlankLoading />
  }

  // @ts-expect-error - Iconoir type definition doesn't support dynamic access
  const Logo = Iconoir[call.task.iconoir_logo]

  let taskType = "normal"
  if (call.task.slug === "prank_call") {
    taskType = "prank"
  } else {
    if (call.dynamic_task) {
      taskType = "dynamic"
    }
    if (call.task_template) {
      taskType = "template"
    }
  }

  const showLoading = call == null || taskSvc == null || recSvc == null || callerSettings == null || plan == null

  const callService = new CallService(call)

  return (
    <div className="flex flex-col">
      {showLoading && <LoadingFullPageOverlay />}
      {/* Add this near your other modals */}
      <ConsentModal
        show={showConsentModal}
        onClose={() => setShowConsentModal(false)}
        onConfirm={() => {
          // Create a synthetic form event to pass to placeCall
          const e = new Event("submit") as unknown as React.FormEvent
          Object.defineProperty(e, "target", { value: document.getElementById("callSettingsForm") })

          setShowConsentModal(false)
          placeCall(e)
        }}
      />
      <LanguageMismatchModal
        show={showLanguageMismatchModal}
        onClose={() => setShowLanguageMismatchModal(false)}
        selectedLocale={selectedLocale}
        defaultLocale={defaultLocale}
        onConfirm={async () => {
          setShowLanguageMismatchModal(false)
          if (formEventForLanguageMismatch) {
            if (call.task_slug === "prank_call") {
              setShowConsentModal(true)
              return
            }
            await placeCall(formEventForLanguageMismatch)
          }
        }}
      />
      <CallerSettingsModal
        show={showCallerSettingsModal}
        setShow={setShowCallerSettingsModal}
        reload={reloadCallerSettingsCallback}
      />
      <ScheduleCallModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSchedule={(newScheduledAt) => {
          setScheduledAt(newScheduledAt)
          setShowScheduleModal(false)
        }}
        initialScheduledAt={scheduledAt}
      />
      {showVerifyCallbackNumberModal && (
        <VerifyPhoneNumberModal
          show={showVerifyCallbackNumberModal}
          setShow={setShowVerifyCallbackNumberModal}
          mode="callback"
          onClose={() => setShowVerifyCallbackNumberModal(false)}
          onPhoneNumberVerified={async () => {
            setShowVerifyCallbackNumberModal(false)
            // Refresh the list after successful confirmation
            vibratoClient.getCallbackPhoneNumbers().then(setCallbackPhoneNumbers)
          }}
        />
      )}
      {call && call.task && taskSvc && recSvc && (
        <>
          <form onSubmit={handleSubmit} id="callSettingsForm">
            <div className="flex flex-col">
              <TaskInfoCard
                call={call}
                taskType={taskType}
                callService={callService}
                recSvc={recSvc}
                scheduledAt={scheduledAt}
                setShowScheduleModal={setShowScheduleModal}
                setShowDynamicTaskModal={setShowDynamicTaskModal}
                audioElement={audioElement}
                setAudioElement={setAudioElement}
                playingVoiceSlug={playingVoiceSlug}
                setPlayingVoiceSlug={setPlayingVoiceSlug}
                loadingVoiceSlug={loadingVoiceSlug}
                setLoadingVoiceSlug={setLoadingVoiceSlug}
                vibratoClient={vibratoClient}
              />

              <CallSettingsCard
                call={call}
                callerSettings={callerSettings}
                twilioPhoneNumbers={twilioPhoneNumbers}
                hiddenSections={hiddenSections}
                restoreSection={restoreSection}
                removedSections={removedSections}
                triggerSettingsTour={triggerSettingsTour}
                setShowCallerSettingsModal={setShowCallerSettingsModal}
                reloadCallerSettings={reloadCallerSettingsCallback}
                callbackPhoneNumbers={callbackPhoneNumbers}
                setShowVerifyCallbackNumberModal={setShowVerifyCallbackNumberModal}
              />

              <CallInformationCard fields={allCallInfoFields} values={allCallInfoValues} />
            </div>
          </form>
        </>
      )}
    </div>
  )
}

function ConsentModal({ show, onClose, onConfirm }: { show: boolean; onClose: () => void; onConfirm: () => void }) {
  // const navigate = useNavigate()
  return (
    <Modal show={show} onClose={onClose} className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <span className="text-purple-600">Consent Required for Prank Call</span>
      </ModalHeader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <p className="text-gray-700">
            Before proceeding with this prank call, please confirm that you have explicit consent from the person
            you&apos;re calling. Making prank calls without consent may be illegal in some jurisdictions.
          </p>
          <p className="text-gray-700">Do you have consent from the person you&apos;re calling?</p>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              onClose()
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50"
          >
            No, Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-full hover:bg-blue-700"
          >
            Yes, I Have Consent
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

function LanguageMismatchModal({
  show,
  onClose,
  onConfirm,
  selectedLocale,
  defaultLocale
}: {
  show: boolean
  onClose: () => void
  onConfirm: () => void
  selectedLocale: string | null
  defaultLocale: string | null
}) {
  return (
    <Modal show={show} onClose={onClose} size="xl" className="pt-safe">
      <ModalBody className="p-0">
        <div className="flex flex-col">
          {/* Header Section with Gradient Background */}
          <div className="bg-linear-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-t-lg">
            <div className="flex items-center justify-center mb-3">
              <div className="bg-white/20 p-2 rounded-full">
                <Iconoir.Language className="w-6 h-6" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-center">Language Mismatch</h2>
          </div>
          <div className="p-6 text-center">
            <p className="text-gray-700 text-base">
              You've selected{" "}
              <span className="font-semibold text-purple-600">
                {selectedLocale && localeInfo[selectedLocale].displayName}
              </span>
              , but the country's default is{" "}
              <span className="font-semibold text-purple-600">
                {defaultLocale && localeInfo[defaultLocale].displayName}
              </span>
              .
            </p>
            <p className="text-gray-600 mt-4">
              The AI will speak in the language you selected. Do you want to continue?
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="justify-center">
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-gray-600 font-semibold rounded-full hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="inline-flex items-center px-5 py-2 bg-purple-600 text-white font-semibold rounded-full transition-colors gap-2 hover:bg-purple-700"
          >
            Yes, Continue
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

function callSettingsForTaskRecipient({
  call,
  slug,
  callerSettings,
  disablePhoneFields,
  twilioPhoneNumbers
}: {
  call: Call
  slug: string
  callerSettings: CallerSettings | null
  disablePhoneFields?: boolean
  twilioPhoneNumbers: TwilioPhoneNumber[]
}): FieldSettings[] {
  const supportedLocalesForCallerSettings =
    defaultVoices.find((defaultVoice) => callerSettings?.voice_id === defaultVoice.id)?.supported_locales ||
    supportedLocales

  const callLanguageElement: FieldSettings =
    supportedLocalesForCallerSettings.length <= 1
      ? {
          name: "Call language",
          html_id: "locale",
          html_type: "select",
          value: supportedLocalesForCallerSettings[0],
          options: supportedLocalesForCallerSettings,
          options_display: supportedLocalesForCallerSettings.map((locale) => localeInfo[locale].displayName),
          removable: true,
          description:
            "The language the AI will speak. Match to the country you're calling for best results. For more language options, change the voice in Voice & AI Settings."
        }
      : {
          name: "Call language",
          html_id: "locale",
          html_type: "select",
          default_text: "en-us",
          options: supportedLocalesForCallerSettings,
          options_display: supportedLocalesForCallerSettings.map((locale) => localeInfo[locale].displayName),
          removable: true,
          description:
            "The language the AI will speak. Match to the country you're calling for best results. For more language options, change the voice in Voice & AI Settings."
        }

  let outboundPhoneNumberField: FieldSettings[] = []
  if (twilioPhoneNumbers.length > 0) {
    outboundPhoneNumberField = [
      {
        name: "Outbound phone number",
        html_id: "outbound_twilio_phone_number_uuid",
        html_type: "select",
        options: twilioPhoneNumbers.map((number) => number.uuid),
        options_display: twilioPhoneNumbers.map((number) =>
          formatAnyPhoneNumber(number.country_code, number.phone_number)
        ),
        required: true,
        value: call.outbound_twilio_phone_number_uuid,
        description: "The phone number that will be used to call the recipient.",
        removable: false // Required field, can't be removed
      }
    ]
  }

  if (slug.startsWith("any") && !disablePhoneFields) {
    const settings: FieldSettings[] = [
      {
        name: "Country code",
        html_id: "country_code",
        html_type: "select",
        default_text: "1",
        options: supportedCountryCodes.map((code: string) => countryCodeInfo[code].code),
        options_display: supportedCountryCodes.map((code) => countryCodeInfo[code].displayName),
        removable: false // Required field, can't be removed
      },
      {
        name: "Phone number",
        html_id: "phone_number",
        html_type: "tel",
        placeholder: "(123) 456-7890",
        required: true,
        removable: false // Required field, can't be removed
      }
    ]

    // Only add call language element if not a prank call
    return settings
      .concat(outboundPhoneNumberField)
      .concat(call.task.slug !== "prank_call" ? callLanguageElement : []) as FieldSettings[]
  } else {
    const settings: FieldSettings[] = [
      {
        name: "Country code",
        html_id: "country_code",
        html_type: "select",
        default_text: "1",
        options: supportedCountryCodes.map((code: string) => countryCodeInfo[code].code),
        options_display: supportedCountryCodes.map((code) => countryCodeInfo[code].displayName),
        disabled: true,
        removable: false // Required field, can't be removed
      },
      {
        name: "Phone number",
        html_id: "phone_number",
        html_type: "tel",
        placeholder: "(123) 456-7890",
        required: true,
        disabled: true,
        removable: false // Required field, can't be removed
      }
    ]

    // Only add call language element if not a prank call
    return settings
      .concat(outboundPhoneNumberField)
      .concat(call.task.slug !== "prank_call" ? callLanguageElement : []) as FieldSettings[]
  }
}
